<!-- Embedded pane of the chat workspace — no page breadcrumb; the back button
     beside the term field returns to the conversation. -->
<div class="row">
  <div class="col-12">
    <div class="card">
      <div class="card-body">

        <!-- Term + filters -->
        <div class="d-flex align-items-center gap-2 mb-3">
          <button
            type="button"
            class="btn btn-light flex-shrink-0"
            [matTooltip]="'chat.search.back' | translate"
            [attr.aria-label]="'chat.search.back' | translate"
            (click)="backToWorkspace()"
          >
            <i class="ri-arrow-left-line"></i>
          </button>
          <div class="flex-grow-1">
            <input
              type="text"
              class="form-control"
              name="chatSearchTerm"
              autocomplete="off"
              [placeholder]="'chat.search.placeholder' | translate"
              [attr.aria-label]="'chat.search.placeholder' | translate"
              [(ngModel)]="term"
              (input)="search()"
            >
          </div>
        </div>

        <div class="row g-2 mb-2">
          <div class="col-md-3">
            <label class="form-label">{{ 'chat.search.filterFrom' | translate }}</label>
            <select class="form-select" name="chatSearchFrom" [(ngModel)]="filterFrom" (change)="search()">
              <option value="">{{ 'chat.search.anyone' | translate }}</option>
              @for (option of peopleOptions; track trackOption($index, option)) {
                <option [value]="option.id">{{ option.label }}</option>
              }
            </select>
          </div>

          <div class="col-md-3">
            <label class="form-label">{{ 'chat.search.filterIn' | translate }}</label>
            <select class="form-select" name="chatSearchIn" [(ngModel)]="filterIn" (change)="search()">
              <option value="">{{ 'chat.search.anyConversation' | translate }}</option>
              @for (option of conversationOptions; track trackOption($index, option)) {
                <option [value]="option.id">{{ option.label }}</option>
              }
            </select>
          </div>

          <div class="col-md-2">
            <label class="form-label">{{ 'chat.search.filterType' | translate }}</label>
            <select class="form-select" name="chatSearchType" [(ngModel)]="filterType" (change)="search()">
              <option value="">{{ 'chat.search.anyType' | translate }}</option>
              <option [value]="messageTypes.TEXT">{{ 'chat.search.typeText' | translate }}</option>
              <option [value]="messageTypes.IMAGE">{{ 'chat.search.typeImage' | translate }}</option>
              <option [value]="messageTypes.AUDIO">{{ 'chat.search.typeAudio' | translate }}</option>
              <option [value]="messageTypes.VIDEO">{{ 'chat.search.typeVideo' | translate }}</option>
              <option [value]="messageTypes.DOCUMENT">{{ 'chat.search.typeDocument' | translate }}</option>
            </select>
          </div>

          <div class="col-md-2">
            <label class="form-label">{{ 'chat.search.filterDateFrom' | translate }}</label>
            <div class="input-group">
              <input
                class="form-control"
                name="chatSearchDateFrom"
                readonly
                [matDatepicker]="dateFromPicker"
                [placeholder]="'chat.search.datePlaceholder' | translate"
                [attr.aria-label]="'chat.search.filterDateFrom' | translate"
                [(ngModel)]="filterDateFrom"
                (click)="dateFromPicker.open()"
                (dateChange)="search()"
              >
              <button
                type="button"
                class="btn btn-light"
                [attr.aria-label]="'chat.search.filterDateFrom' | translate"
                (click)="dateFromPicker.open()"
              >
                <i class="ri-calendar-line"></i>
              </button>
              <mat-datepicker #dateFromPicker></mat-datepicker>
            </div>
          </div>

          <div class="col-md-2">
            <label class="form-label">{{ 'chat.search.filterDateTo' | translate }}</label>
            <div class="input-group">
              <input
                class="form-control"
                name="chatSearchDateTo"
                readonly
                [matDatepicker]="dateToPicker"
                [placeholder]="'chat.search.datePlaceholder' | translate"
                [attr.aria-label]="'chat.search.filterDateTo' | translate"
                [(ngModel)]="filterDateTo"
                (click)="dateToPicker.open()"
                (dateChange)="search()"
              >
              <button
                type="button"
                class="btn btn-light"
                [attr.aria-label]="'chat.search.filterDateTo' | translate"
                (click)="dateToPicker.open()"
              >
                <i class="ri-calendar-line"></i>
              </button>
              <mat-datepicker #dateToPicker></mat-datepicker>
            </div>
          </div>
        </div>

        <!-- Active filter chips -->
        @if (hasFilters) {
          <div class="d-flex align-items-center flex-wrap gap-1 mb-3">
            @for (chip of chips; track chip.key) {
              <span class="badge bg-soft-primary text-primary d-inline-flex align-items-center gap-1">
                <span>{{ chip.label }}</span>
                <button
                  type="button"
                  class="btn btn-link btn-sm p-0 text-primary chat-search__chip-remove"
                  [attr.aria-label]="'chat.search.removeFilter' | translate"
                  (click)="removeChip(chip)"
                >
                  <i class="ri-close-line"></i>
                </button>
              </span>
            }
            <button type="button" class="btn btn-sm btn-light" (click)="clearFilters()">
              {{ 'chat.search.clearFilters' | translate }}
            </button>
          </div>
        }

        <!-- Result tabs -->
        <ul class="nav nav-tabs mb-2">
          <li class="nav-item">
            <a href="javascript:void(0);" class="nav-link" [class.active]="activeTab === 'messages'" (click)="selectTab('messages')">
              <i class="ri-message-2-line me-1"></i>{{ 'chat.search.tabMessages' | translate }}
              <span class="badge bg-soft-secondary text-secondary ms-1">{{ messageRows.length }}</span>
            </a>
          </li>
          <li class="nav-item">
            <a href="javascript:void(0);" class="nav-link" [class.active]="activeTab === 'conversations'" (click)="selectTab('conversations')">
              <i class="ri-chat-3-line me-1"></i>{{ 'chat.search.tabConversations' | translate }}
              <span class="badge bg-soft-secondary text-secondary ms-1">{{ conversationRows.length }}</span>
            </a>
          </li>
          <li class="nav-item">
            <a href="javascript:void(0);" class="nav-link" [class.active]="activeTab === 'people'" (click)="selectTab('people')">
              <i class="ri-user-search-line me-1"></i>{{ 'chat.search.tabPeople' | translate }}
              <span class="badge bg-soft-secondary text-secondary ms-1">{{ peopleRows.length }}</span>
            </a>
          </li>
        </ul>

        <!-- States -->
        @if (loading) {
          <div class="d-flex flex-column align-items-center justify-content-center gap-2 py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <span class="text-muted">{{ 'chat.search.loading' | translate }}</span>
          </div>
        } @else if (hasError) {
          <div class="d-flex flex-column align-items-center justify-content-center gap-2 py-5 text-center">
            <i class="ri-error-warning-line font-24 text-danger"></i>
            <span class="text-muted">{{ 'chat.search.loadError' | translate }}</span>
            <button type="button" class="btn btn-sm btn-soft-primary" (click)="search()">
              {{ 'chat.search.retry' | translate }}
            </button>
          </div>
        } @else if (!searched) {
          <div class="d-flex flex-column align-items-center justify-content-center gap-2 py-5 px-3 text-center">
            <div class="avatar-sm rounded-circle bg-soft-primary">
              <i class="ri-search-2-line avatar-title text-primary font-18"></i>
            </div>
            <span class="text-muted">{{ 'chat.search.prompt' | translate }}</span>
            <small class="text-muted">{{ 'chat.search.promptHint' | translate }}</small>
          </div>
        } @else if (!hasAnyResult) {
          <div class="d-flex flex-column align-items-center justify-content-center gap-2 py-5 px-3 text-center">
            <div class="avatar-sm rounded-circle bg-soft-primary">
              <i class="ri-inbox-line avatar-title text-primary font-18"></i>
            </div>
            <span class="text-muted">{{ 'chat.search.empty' | translate }}</span>
            <small class="text-muted">{{ 'chat.search.emptyHint' | translate }}</small>
          </div>
        } @else {

          <!-- Messages -->
          @if (activeTab === 'messages') {
            @if (messageRows.length === 0) {
              <p class="text-muted mb-0 py-3">{{ 'chat.search.emptyMessages' | translate }}</p>
            } @else {
              <div class="list-group list-group-flush">
                @for (row of messageRows; track trackMessage($index, row)) {
                  <button
                    type="button"
                    class="list-group-item list-group-item-action d-flex gap-2 py-2"
                    [disabled]="!row.openable"
                    (click)="openMessage(row)"
                  >
                    @if (row.authorImage) {
                      <img class="avatar-xs rounded-circle flex-shrink-0" [src]="row.authorImage" [alt]="row.authorName">
                    } @else {
                      <span class="avatar-xs rounded-circle bg-soft-primary flex-shrink-0">
                        <span class="avatar-title text-primary">{{ row.authorInitial }}</span>
                      </span>
                    }
                    <span class="flex-grow-1 overflow-hidden text-start">
                      <span class="d-flex align-items-center flex-wrap gap-1">
                        <span class="fw-semibold chat-name">{{ row.authorName }}</span>
                        <small class="text-muted d-inline-flex align-items-center gap-1">
                          <i [ngClass]="row.conversationIcon"></i>{{ row.conversationName }}
                        </small>
                        <small class="text-muted">·</small>
                        <small class="text-muted">{{ row.dateLabel }}</small>
                        <i class="text-muted" [ngClass]="row.typeIcon"></i>
                      </span>
                      <small class="d-block chat-search__excerpt">
                        @for (segment of row.segments; track trackSegment($index, segment)) {
                          @if (segment.match) {
                            <mark>{{ segment.text }}</mark>
                          } @else {
                            <span>{{ segment.text }}</span>
                          }
                        }
                      </small>
                    </span>
                    @if (row.openable) {
                      <i class="ri-arrow-right-line text-muted flex-shrink-0"></i>
                    }
                  </button>
                }
              </div>
            }
          }

          <!-- Conversations -->
          @if (activeTab === 'conversations') {
            @if (conversationRows.length === 0) {
              <p class="text-muted mb-0 py-3">{{ 'chat.search.emptyConversations' | translate }}</p>
            } @else {
              <div class="list-group list-group-flush">
                @for (row of conversationRows; track trackConversation($index, row)) {
                  <button
                    type="button"
                    class="list-group-item list-group-item-action d-flex align-items-center gap-2 py-2"
                    [disabled]="!row.isMember"
                    (click)="openConversation(row)"
                  >
                    <span class="avatar-xs rounded-circle bg-soft-primary flex-shrink-0">
                      <i class="avatar-title text-primary font-16" [ngClass]="row.icon"></i>
                    </span>
                    <span class="flex-grow-1 overflow-hidden text-start text-truncate">
                      @for (segment of row.segments; track trackSegment($index, segment)) {
                        @if (segment.match) {
                          <mark>{{ segment.text }}</mark>
                        } @else {
                          <span>{{ segment.text }}</span>
                        }
                      }
                    </span>
                    @if (row.isMember) {
                      <i class="ri-arrow-right-line text-muted flex-shrink-0"></i>
                    } @else {
                      <span class="badge bg-soft-secondary text-secondary flex-shrink-0">
                        {{ 'chat.search.notMember' | translate }}
                      </span>
                    }
                  </button>
                }
              </div>
            }
          }

          <!-- People -->
          @if (activeTab === 'people') {
            @if (peopleRows.length === 0) {
              <p class="text-muted mb-0 py-3">{{ 'chat.search.emptyPeople' | translate }}</p>
            } @else {
              <div class="list-group list-group-flush">
                @for (row of peopleRows; track trackPerson($index, row)) {
                  <button
                    type="button"
                    class="list-group-item list-group-item-action d-flex align-items-center gap-2 py-2"
                    (click)="openPerson(row)"
                  >
                    @if (row.image) {
                      <img class="avatar-xs rounded-circle flex-shrink-0" [src]="row.image" [alt]="row.name">
                    } @else {
                      <span class="avatar-xs rounded-circle bg-soft-primary flex-shrink-0">
                        <span class="avatar-title text-primary">{{ row.initial }}</span>
                      </span>
                    }
                    <span class="flex-grow-1 overflow-hidden text-start">
                      <span class="d-block text-truncate fw-semibold">
                        @for (segment of row.segments; track trackSegment($index, segment)) {
                          @if (segment.match) {
                            <mark>{{ segment.text }}</mark>
                          } @else {
                            <span>{{ segment.text }}</span>
                          }
                        }
                      </span>
                      @if (row.email) {
                        <small class="text-muted d-block text-truncate">{{ row.email }}</small>
                      }
                    </span>
                    <i class="ri-chat-3-line text-muted flex-shrink-0"></i>
                  </button>
                }
              </div>
            }
          }

        }

      </div>
    </div>
  </div>
</div>
